import { OrganizationUserDto } from "./ObjectTypes";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "./TailwindClassnames";
import Pagination from "../common/Pagination";

/**
 * Props for the ActiveUsersTable component.
 */
type ActiveUsersTableProps = {
  activeUsers: OrganizationUserDto[];
  onSelectUser: Function;
  take: number;
  totalCount: number;
  resultsLoaded: boolean;
  offset: number;
  onPageClick: Function;
};

/**
 * A table component that displays a list of active users. Renders its own
 * `tailwindClassnames.tableCard` surface with the pagination footer inside
 * the card — consumers (org/project users tabs) must not wrap it in another
 * card container.
 */
const ActiveUsersTable: React.FC<ActiveUsersTableProps> = ({
  activeUsers,
  onSelectUser,
  take,
  totalCount,
  resultsLoaded,
  offset,
  onPageClick,
}) => {
  const { t } = useTranslation();
  return (
    <div className={tailwindClassnames.tableCard}>
      <table className="w-full">
        <thead>
          <tr>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {t("Name")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {t("Role")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {t("Email")}
            </th>
          </tr>
        </thead>
        <tbody>
          {activeUsers.length === 0 ? (
            <tr>
              <td
                colSpan={3}
                className="px-4 py-4 text-center text-off-black-light dark:text-off-white-dark transition duration-300"
              >
                {t("No Data Found")}
              </td>
            </tr>
          ) : (
            activeUsers.map((user, index) => {
              return (
                <tr
                  key={index}
                  onClick={() => onSelectUser(user.user_id)}
                  className={tailwindClassnames.tableCardRow}
                >
                  <td
                    className={tailwindClassnames.tableCardCellPrimary}
                    title={
                      `${user.first_name ?? ""} ${
                        user.last_name ?? ""
                      }`.trim() || undefined
                    }
                  >
                    {user.first_name + " " + user.last_name}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={t(user.role_name) || undefined}
                  >
                    {t(user.role_name)}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={user.email ?? undefined}
                  >
                    {user.email}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <Pagination
        showText={true}
        handlePageClick={onPageClick}
        pageCount={Math.ceil(totalCount / take)}
        resultStart={offset + 1}
        initialPage={Math.floor(offset / take)}
        resultEnd={offset + take <= totalCount ? offset + take : totalCount}
        totalCount={totalCount}
        take={take}
        loading={!resultsLoaded}
        mainclassname={tailwindClassnames.tableCardFooter}
      />
    </div>
  );
};

export default ActiveUsersTable;
